import type { QuestionLibraryCatalogCourse } from 'services/api/adminContentClient';

import { QUESTION_TYPE_LABEL_BY_SLUG } from './types';

export type QuestionLibraryMode = 'course' | 'search';

export const UNASSIGNED_COURSE = 'unassigned' as const;
export type BrowserCourseId = number | typeof UNASSIGNED_COURSE;

/** Flatten stored Markdown/callback text into a one-line label for lists and trees. */
export const plainQuestionLabel = (value: string | null | undefined): string =>
  String(value ?? '')
    .replace(/\{\{[^}]*\}\}/g, '…')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[#*_>`|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const formatQuestionTypeSlug = (slug: string | null | undefined): string => {
  if (!slug) {
    return '-';
  }
  return (
    QUESTION_TYPE_LABEL_BY_SLUG[slug]
    ?? slug.replace(/[_\s]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()).trim()
  );
};

/** Life Plan first, then the remaining real courses by name; likely test copies are kept apart. */
export const groupCatalogCourses = (courses: QuestionLibraryCatalogCourse[]) => {
  const byName = (a: QuestionLibraryCatalogCourse, b: QuestionLibraryCatalogCourse) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.id - b.id;
  const primary = courses
    .filter((course) => !course.looks_like_test)
    .sort((a, b) => Number(b.is_life_plan) - Number(a.is_life_plan) || byName(a, b));
  const tests = courses
    .filter((course) => course.looks_like_test)
    .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '') || byName(a, b));
  return { primary, tests };
};

export const parseBrowserCourseId = (value: string | null): BrowserCourseId | null => {
  if (value === UNASSIGNED_COURSE) {
    return UNASSIGNED_COURSE;
  }
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const formatEditedAgo = (iso: string | null | undefined, now: Date = new Date()): string => {
  if (!iso) {
    return '';
  }
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) {
    return '';
  }
  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (days < 1) {
    return 'today';
  }
  if (days < 14) {
    return `${days}d ago`;
  }
  if (days < 60) {
    return `${Math.round(days / 7)}w ago`;
  }
  if (days < 730) {
    return `${Math.round(days / 30)}mo ago`;
  }
  return `${Math.round(days / 365)}y ago`;
};
